/**
 * JSON Layout Type Constants
 *
 * Single source of truth for all JSON output type strings.
 * These are the types used in the portable JSON format consumed by renderers.
 *
 * Use these constants instead of string literals to ensure type safety
 * and make refactoring easier.
 *
 * @example
 * ```typescript
 * import { JSON_TYPES } from './constants/json-types';
 *
 * const layoutItem = {
 *   id: 'text_1',
 *   type: JSON_TYPES.HIGHLIGHTED_TEXT,
 *   value: "123"
 * };
 * ```
 */

/**
 * JSON Layout Item Types
 *
 * These are the types used in the JSON output format.
 */
export const JSON_TYPES = {
  // ═══════════════════════════════════════════════════════════════════════════
  // Layout Items
  // ═══════════════════════════════════════════════════════════════════════════

  /** Question/instruction text */
  QUESTION_TEXT: 'question-text',

  /** Regular text element */
  TEXT: 'text',

  /** Spacing element */
  SPACE: 'space',

  /** Text with optional highlighting */
  HIGHLIGHTED_TEXT: 'highlighted-text',

  /** Text with color styling */
  COLOR_TEXT: 'color-text',

  /** Bold text */
  BOLD_TEXT: 'bold-text',

  /** Inline image */
  IMAGE: 'image',

  /** Reference to a widget */
  WIDGET_REF: 'widget-ref',

  /** Clock widget (inline): analog or digital, display or interactive */
  CLOCK: 'clock',

  /** Line break element */
  LINEBREAK: 'linebreak',

  /** Grouped layout items */
  GROUP: 'group',

  /** Operator symbol */
  OPERATOR: 'operator',

  /** Table layout block */
  TABLE: 'table',

  /** Row within a table */
  TABLE_ROW: 'table-row',

  /** Cell within a table */
  TABLE_CELL: 'table-cell',

  /** Vertical operation (column arithmetic) block */
  VERTICAL_OPERATION: 'vertical-operation',

  /** Row within a vertical operation */
  VOP_ROW: 'vop-row',

  /** Cell within a vertical-operation row */
  VOP_CELL: 'vop-cell',

  /** Separator within a vertical operation */
  VOP_SEPARATOR: 'vop-separator',

  /** Carry row within a vertical operation */
  VOP_CARRY_ROW: 'vop-carry-row',

  /** Number line layout block */
  NUMBER_LINE: 'number-line',

  /** Position marker within a number line */
  NUMBER_LINE_POSITION: 'number-line-position',

  /** Dynamic subdivision counter embedded in a number line */
  NUMBER_LINE_COUNTER: 'number-line-counter',

  /** Highlighted range within a number line */
  NUMBER_LINE_HIGHLIGHT: 'number-line-highlight',

  /** Draggable cursor within a number line */
  NUMBER_LINE_CURSOR: 'number-line-cursor',
  /** Arrow annotation within a number line */
  NUMBER_LINE_ARROW: 'number-line-arrow',

  /** Matcher layout block */
  MATCHER: 'matcher',

  /** Row within a matcher */
  MATCHER_ROW: 'matcher-row',

  /** Item within a matcher row */
  MATCHER_ITEM: 'matcher-item',
  /** Pair-matcher widget (top-row ↔ bottom-row pairing) */
  PAIR_MATCHER: 'pair-matcher',
  // ═══════════════════════════════════════════════════════════════════════════
  // Widget Types
  // ═══════════════════════════════════════════════════════════════════════════

  /** Inline input widget (string or numeric) */
  INLINE_INPUT: 'inline-input',

  /** Drag-and-drop options widget */
  DRAG_OPTIONS: 'drag-options',

  /** Single inline draggable option (appears in-flow with text) */
  INLINE_DRAG_OPTION: 'inline-drag-option',

  /** Radio button options widget */
  RADIO_OPTIONS: 'radio-options',

  /** Numeric keypad widget */
  NUMERIC_KEYPAD: 'numeric-keypad',

  /** Inline fraction widget */
  FRACTION: 'fraction',

  /** Visual fraction widget */
  VISUAL_FRACTION: 'visual-fraction',

  /** Ruler geometry tool widget */
  RULER: 'ruler',

  /** Protractor geometry tool widget */
  PROTRACTOR: 'protractor',

  /** Set square geometry tool widget */
  SET_SQUARE: 'set-square',

} as const;

/**
 * Reserved widget ID used by the transformer to store the drag-options widget.
 * Components detect drag mode by checking `widgets[DRAG_OPTIONS_WIDGET_ID]`.
 */
export const DRAG_OPTIONS_WIDGET_ID = '_drag_options' as const;

/**
 * Reserved widget ID for the ruler geometry tool.
 */
export const RULER_WIDGET_ID = '_ruler' as const;

/**
 * Reserved widget ID for the protractor geometry tool.
 */
export const PROTRACTOR_WIDGET_ID = '_protractor' as const;

/**
 * Reserved widget ID for the set square geometry tool.
 */
export const SET_SQUARE_WIDGET_ID = '_set_square' as const;

/**
 * Widget types that carry internal state and must be remounted (key change)
 * when the exercise is reset. Add a new type here to opt it in automatically;
 * no other file needs to change.
 */
export const STATEFUL_WIDGET_TYPES: Set<string> = new Set([
  JSON_TYPES.MATCHER,
  JSON_TYPES.PAIR_MATCHER,
  JSON_TYPES.VISUAL_FRACTION,
]);
/**
 * Prefix used to build widget IDs for inline-drag-option chips.
 * Each distinct chip value gets its own entry: `${INLINE_DRAG_OPT_PREFIX}<value>`.
 *
 * @example
 * `${INLINE_DRAG_OPT_PREFIX}0` → '_inline_drag_opt_0'
 */
export const INLINE_DRAG_OPT_PREFIX = '_inline_drag_opt_' as const;
/**
 * Layout item types that are CSS boxes (not pure text).
 * These need `dir="rtl"` on their line container to be ordered correctly
 * in an RTL context — the Unicode Bidi Algorithm only reorders characters,
 * not DOM elements.
 *
 * Add new types here whenever a new inline CSS-box widget is introduced.
 */
export const RTL_INLINE_WIDGET_TYPES = new Set<string>([
  JSON_TYPES.INLINE_INPUT,
  JSON_TYPES.INLINE_DRAG_OPTION,
  JSON_TYPES.FRACTION,
  JSON_TYPES.VISUAL_FRACTION,
]);

/**
 * Type helper to get all JSON type values as a union type
 */
export type JSONType = typeof JSON_TYPES[keyof typeof JSON_TYPES];
